<script setup lang="ts">
/**
 * PlanChangeBanner - the in-app half of the plan-change announcement.
 *
 * Before FREE_PLAN_CHANGE_AT it is shown to legacy Free tenants only (what
 * changes for them), and for PLAN_CHANGE_BANNER_DAYS_AFTER days after it to
 * everyone (what is new). `planChangeBannerVariant()` decides; the API says
 * whether the tenant is legacy Free in `usageStatus.legacyFreeAllowance`.
 *
 * Self-contained so an app mounts it in one line: the app passes the usage it
 * already holds and its docs origin, and answers `billing` with however it
 * reaches the company portal's Billing page (the extension cannot open the
 * portal with a plain link inside the Lark client).
 *
 * Dismissal is per variant, so someone who closed the warning still sees the
 * "it happened" banner once. It is remembered in localStorage; where that is
 * unavailable (private window, blocked storage) the banner simply comes back
 * on the next load.
 */
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { X } from 'lucide-vue-next'
import { docsUrl, type UsageStatus } from '@lark-automadoc/shared-types'
import {
  PLAN_CHANGE_ANNOUNCEMENT_ID,
  PLAN_CHANGE_DOCS_PATH,
  planChangeBannerVariant,
  type PlanChangeBannerVariant,
} from '../../announcements/planChange'

const props = defineProps<{
  usageStatus: UsageStatus | null
  /** The app's VITE_DOCS_URL; unset is production. */
  docsOrigin?: string | null
  /** The viewer is an app admin; only they get the Billing link. Omitted counts as one. */
  canPurchase?: boolean
}>()

const emit = defineEmits<{ billing: [] }>()

const { t, locale } = useI18n()

const storageKey = (variant: PlanChangeBannerVariant) => `automadoc-${PLAN_CHANGE_ANNOUNCEMENT_ID}-${variant}-dismissed`

function readDismissed(variant: PlanChangeBannerVariant): boolean {
  try {
    return localStorage.getItem(storageKey(variant)) === 'true'
  } catch {
    return false
  }
}

const variant = computed(() => planChangeBannerVariant(props.usageStatus))
const dismissed = ref(false)
watch(variant, (v) => { dismissed.value = v ? readDismissed(v) : false }, { immediate: true })

function dismiss(): void {
  dismissed.value = true
  if (!variant.value) return
  try {
    localStorage.setItem(storageKey(variant.value), 'true')
  } catch {
    // Not remembered; it shows again next time, which is the safe side.
  }
}

const whatsNewUrl = computed(() => docsUrl(PLAN_CHANGE_DOCS_PATH, locale.value, props.docsOrigin))
// After the change, buying is only worth offering to someone the quota binds.
const showBilling = computed(
  () => props.canPurchase !== false && (variant.value === 'before' || props.usageStatus?.quotaEnforced === true),
)
</script>

<template>
  <div
    v-if="variant && !dismissed"
    class="flex items-start gap-3 px-6 py-3 border-b bg-teal-50 border-teal-200 dark:bg-teal-900/30 dark:border-teal-800"
    role="status"
  >
    <div class="flex-1 min-w-0">
      <div class="text-sm font-medium text-gray-800 dark:text-gray-100">{{ t('announcementPlanChangeTitle') }}</div>
      <div class="text-xs text-gray-700 dark:text-gray-300 mt-0.5">
        {{ variant === 'before' ? t('announcementPlanChangeBannerBefore') : t('announcementPlanChangeBannerAfter') }}
      </div>
      <div class="flex flex-wrap gap-3 mt-1.5 text-xs font-medium">
        <a
          :href="whatsNewUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="text-teal-700 hover:text-teal-900 dark:text-teal-300 dark:hover:text-teal-100 underline"
        >{{ t('announcementPlanChangeWhatsNew') }}</a>
        <button
          v-if="showBilling"
          type="button"
          class="text-teal-700 hover:text-teal-900 dark:text-teal-300 dark:hover:text-teal-100 underline"
          @click="emit('billing')"
        >{{ t('announcementPlanChangeBilling') }}</button>
      </div>
    </div>
    <button
      type="button"
      class="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
      :aria-label="t('close')"
      @click="dismiss"
    >
      <X :size="18" />
    </button>
  </div>
</template>
